import { ApiError } from './api.js';

/** What a user sees when the network itself failed, rather than the server answering. */
const NETWORK_MESSAGE = 'Could not reach the server. Check your connection and try again.';

const DEFAULT_MESSAGE = 'Something went wrong. Please try again.';

/**
 * The message to show a user for a caught error.
 *
 * Two local copies of this existed, and 81 call sites inlined the check by hand. About 35 of those
 * ended in `String(e)`, which renders whatever was thrown straight into the UI - a stack, an upstream
 * body, or in one real case the internal sentinel `__WATCH__`. .claude/rules/security.md forbids
 * exposing internal error detail, so an `ApiError`'s mapped message is used when there is one and a
 * human fallback otherwise. The raw error is never rendered.
 *
 * `ApiError` messages ARE safe to show: `req()` maps 5xx to "Service temporarily unavailable" and 429
 * to a rate-limit message, and anything else is the server's own user-facing `error.message`.
 */
export function errMsg(e: unknown, fallback: string = DEFAULT_MESSAGE): string {
  if (e instanceof ApiError) return e.message || fallback;
  // `fetch` rejects with a TypeError when the request never reached the server. That is worth saying
  // plainly, because "try again" is genuinely the right advice and a generic message hides it.
  if (e instanceof TypeError) return NETWORK_MESSAGE;
  return fallback;
}

/**
 * As above, but says the useful thing for an auth failure rather than repeating the server's
 * wording. Use on surfaces that are reachable while signed out.
 */
export function authAwareErrMsg(e: unknown, fallback: string = DEFAULT_MESSAGE): string {
  // 401 and 403 are DIFFERENT answers and must not share a message. 401 means "we do not know who
  // you are", where "sign in" is the right remedy. 403 means the server knew exactly who you were
  // and said no - telling that user to sign in names a remedy they cannot act on, and they are
  // already signed in, so it reads as the product being broken. This is the same defect the
  // codebase already fixed for "session expired" on a plain 401, one status code along.
  if (e instanceof ApiError && e.status === 401) {
    return 'Sign in to use this feature.';
  }
  if (e instanceof ApiError && e.status === 403) {
    // Prefer the server's own wording, which can say WHY (a missing permission, an exhausted
    // allowance). Only fall back when it sent nothing useful.
    return e.message || 'You do not have access to this. If you just signed in, reload and try again.';
  }
  return errMsg(e, fallback);
}
